import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTable } from '../Hooks';
import { collapseEventBuilder, dataSorter, getColSpan, und } from '../Utils';
import { Collapsible } from './Collapsible';
import { Column as ColumnHead } from './Column';
import {
	TableRow,
	TableCell,
	NestedColumnAlgorithm,
	NestedCellAlgorithm,
} from './Extras';
import { DataEmpty } from './DataEmpty';
import { Facade } from './Facade';
const TableContentProps = {
	className: PropTypes.string,
	as: PropTypes.shape({
		Row: PropTypes.elementType,
		Cell: PropTypes.elementType,
		Column: PropTypes.elementType,
		Empty: PropTypes.elementType,
	}),
};

const TableContentDefault = {
	as: {},
};

export const TableContent = ({
	as: {
		Row = TableRow,
		Cell = TableCell,
		Column = ColumnHead,
		Empty = DataEmpty,
		Faking = Facade,
	},
	className,
}) => {
	const [collapse, setCollapse] = useState([]);
	const {
		state: { page, sort, filters, loading },
		data,
		columns,
		pageSize,
		collapsible: {
			render,
			collapseWhen,
			collapseWhenClickRow,
			accordion,
			transition,
		} = {},
		asynchronous,
	} = useTable();

	const colSpan = getColSpan(columns);

	const hasCollapseRender = !!render;

	let source = data;

	if (sort.orderBy && !asynchronous) {
		source = dataSorter(data, sort, columns);
	}

	if (pageSize && pageSize < data.length && !asynchronous) {
		source = source.slice(page * pageSize - pageSize, page * pageSize);
	}

	useEffect(() => {
		setCollapse([]);
	}, [page, sort, filters]);

	return (
		<table className={className} data-loading={loading || und}>
			<thead>
				<NestedColumnAlgorithm
					component={Column}
					columns={columns}
					source={source}
				/>
			</thead>
			<tbody>
				{data.length ? (
					source.map((row, index) => {
						const key = row.key || row.id || `${index}-${page}`;
						const isCollapsible =
							hasCollapseRender && (collapseWhen ? collapseWhen(row) : true);
						const collapseHandler =
							(isCollapsible &&
								collapseEventBuilder(
									key,
									row,
									setCollapse,
									collapseWhen,
									accordion
								)) ||
							und;
						return (
							<Fragment key={key}>
								<Row
									row={row}
									data-collapsible={isCollapsible}
									aria-expanded={isCollapsible ? collapse.includes(key) : und}
									onClick={(collapseWhenClickRow && collapseHandler) || und}>
									<NestedCellAlgorithm
										component={Cell}
										columns={columns}
										row={row}
										index={index}
										actions={{
											page,
											sort,
											filters,
											collapsible: isCollapsible && {
												collapseHandler,
												collapsed: collapse.includes(key),
											},
										}}
									/>
								</Row>
								{isCollapsible && (
									<Collapsible
										colSpan={colSpan}
										render={render}
										row={row}
										index={index}
										transition={transition}
										show={collapse.includes(key)}
									/>
								)}
							</Fragment>
						);
					})
				) : loading ? (
					<Faking pageSize={pageSize || data.length} colSpan={colSpan} />
				) : (
					<Empty colSpan={colSpan} />
				)}
			</tbody>
		</table>
	);
};

TableContent.propTypes = TableContentProps;
TableContent.defaultProps = TableContentDefault;
